import type { MbtiLetter, Persona, PersonaCard, Tally } from "./types.js";

// personality as statement cards, mixed into the deck (plan A).
// Right swipe = "that's me" (+1 to letter); left = mild evidence of the opposite (+0.6).
// Two cards per axis (one per pole) so both directions get positive statements.
const PERSONA_CARD_SEED = [
  {
    id: "p-e",
    type: "persona",
    letter: "E",
    en: "New city, first night: I'm wherever the noise is.",
    media: "lan-kwai-fong",
    g: ["#141A26", "#3E2E6E", "#FF6B9D"],
    em: "🍻",
  },
  {
    id: "p-i",
    type: "persona",
    letter: "I",
    en: "My favorite travel companion is my headphones.",
    media: "p-i",
    g: ["#22302E", "#4A6E66", "#BFD9CC"],
    em: "🍵",
  },
  {
    id: "p-n",
    type: "persona",
    letter: "N",
    en: "Wrong turns are just free side quests.",
    media: "p-n",
    g: ["#33241A", "#8A5A2D", "#E8B45E"],
    em: "🧭",
  },
  {
    id: "p-s",
    type: "persona",
    letter: "S",
    en: "I've read the reviews. All of them.",
    media: "p-s",
    g: ["#10131F", "#2E3A6E", "#E0B14F"],
    em: "🗺️",
  },
  {
    id: "p-f",
    type: "persona",
    letter: "F",
    en: "A stranger's dinner invitation? Always yes.",
    media: "p-f",
    g: ["#3A0F0F", "#8E1F1F", "#E4572E"],
    em: "🤝",
  },
  {
    id: "p-t",
    type: "persona",
    letter: "T",
    en: "The 4am alarm for the sunrise? Worth it. Every time.",
    media: "p-t",
    g: ["#16202E", "#31506E", "#8FB7D9"],
    em: "🏔️",
  },
  {
    id: "p-j",
    type: "persona",
    letter: "J",
    en: "The itinerary is color-coded. Don't touch it.",
    media: "p-j",
    g: ["#26221E", "#5E4F3E", "#C9B08A"],
    em: "📅",
  },
  {
    id: "p-p",
    type: "persona",
    letter: "P",
    en: "The itinerary is a vibe, not a document.",
    media: "p-p",
    g: ["#1E1B2E", "#5A3A6E", "#E0B14F"],
    em: "👃",
  },
];

/** Persona cards carry no taste signal — only MBTI evidence. */
export const PERSONA_CARDS: PersonaCard[] = PERSONA_CARD_SEED.map((c) => ({
  ...c,
  letter: c.letter as MbtiLetter,
  type: "persona" as const,
  g: c.g as [string, string, string],
  ax: { terrain: 0, era: 0, fame: 0, energy: 0, lens: 0 },
  u: { cul: 0, adv: 0, cra: 0, nig: 0, sho: 0 },
}));

export const EMPTY_TALLY: Tally = { E: 0, I: 0, N: 0, S: 0, F: 0, T: 0, J: 0, P: 0 };

export const OPP: Record<MbtiLetter, MbtiLetter> = {
  E: "I",
  I: "E",
  N: "S",
  S: "N",
  F: "T",
  T: "F",
  J: "P",
  P: "J",
};
export const personaFromTally = (t: Tally): Persona => {
  const share = (a: MbtiLetter, b: MbtiLetter): number =>
    t[a] + t[b] > 0 ? t[a] / (t[a] + t[b]) : 0.5;
  return { E: share("E", "I"), N: share("N", "S"), F: share("F", "T"), J: share("J", "P") };
};

export const typeFromPersona = (p: Persona): string =>
  (p.E >= 0.5 ? "E" : "I") +
  (p.N >= 0.5 ? "N" : "S") +
  (p.F >= 0.5 ? "F" : "T") +
  (p.J >= 0.5 ? "J" : "P");
export const personaSim = (a: Persona, b: Persona): number =>
  1 - (Math.abs(a.E - b.E) + Math.abs(a.N - b.N) + Math.abs(a.F - b.F) + Math.abs(a.J - b.J)) / 4;
